import React, { useEffect, useState } from "react";
import { PageHero, Section, SectionTitle, Reveal, CTALink, Eyebrow } from "@/components/Editorial";
import { fetchStatistics } from "@/lib/api";

const CAPABILITIES = [
  { title: "Custom-bonded warehouse", body: "A private customs-bonded facility of approximately 10,000 sq. ft. keeps imported stock secure, compliant and ready for structured release." },
  { title: "Additional warehousing", body: "Over 15,000 sq. ft. of further capacity supports portfolio depth and seasonal planning." },
  { title: "Inventory organisation", body: "Structured storage and rotation discipline protect product condition and traceability." },
  { title: "Import handling & documentation", body: "Customs processes, duties and state-level documentation handled by an experienced in-house team." },
  { title: "Company-owned fleet", body: "Dedicated vehicles provide controlled, dependable movement across Delhi NCR." },
  { title: "Permanent logistics staff", body: "A permanent team — not seasonal contractors — runs receiving, storage and dispatch." },
  { title: "City distribution partners", body: "Beyond NCR, established partners extend reach while HCC maintains supply discipline." },
  { title: "Supply consistency", body: "Fill rates and delivery reliability are treated as brand equity — premium portfolios demand disciplined movement." },
];

export default function InfrastructurePage() {
  const [stats, setStats] = useState([]);

  useEffect(() => {
    fetchStatistics().then(setStats);
  }, []);

  return (
    <div data-testid="infrastructure-page" className="bg-[#F7F1E7]">
      <PageHero
        dark
        eyebrow="Infrastructure"
        title="Structure behind every movement."
        lede="From customs-bonded storage to structured last-mile supply, HCC's infrastructure is built for control, consistency and dependable execution."
      />

      <Section dark className="pt-0">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 max-w-6xl" data-testid="infrastructure-stats">
          {stats.map((s) => (
            <div key={s.id} className="bg-[#101010] px-8 py-10">
              <p className="hcc-serif text-2xl sm:text-3xl text-[#C7A45D]">{s.value}</p>
              <p className="mt-2 text-[11px] uppercase tracking-[0.16em] text-[#F7F1E7]/80">{s.label}</p>
              {s.supporting_text && (
                <p className="mt-3 text-[12px] font-light leading-relaxed text-[#F7F1E7]/50">{s.supporting_text}</p>
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <SectionTitle eyebrow="Capabilities" title="Operational discipline, end to end." />
        <div className="grid sm:grid-cols-2 gap-x-14 gap-y-10 max-w-5xl">
          {CAPABILITIES.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.05}>
              <Eyebrow>{String(i + 1).padStart(2, "0")}</Eyebrow>
              <h3 className="hcc-serif text-xl mt-3 text-[#1c1713]">{c.title}</h3>
              <p className="mt-3 text-sm font-light leading-relaxed text-[#1c1713]/65">{c.body}</p>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section dark className="text-center">
        <Reveal>
          <h2 className="hcc-serif text-3xl font-medium max-w-xl mx-auto">Assess our infrastructure for your brand.</h2>
          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            <CTALink to="/contact" dark solid testId="infrastructure-contact-cta">Send an Enquiry</CTALink>
            <CTALink to="/partner" dark testId="infrastructure-partner-cta">Partner With HCC</CTALink>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
